/**
 * Reusable file attachment hook.
 * Manages pending/uploaded state and uploads via `useUploadFile`.
 * The UI component lives in `@/frontend/components/form/fields` as `FileAttachments`.
 */
import { useState } from 'react'

import { type FileKey, MAX_FILE_ATTACHMENTS } from '@snootsvet/shared'

import type { ExistingFile, FileAttachmentProps } from '@/frontend/components/form/file-attachments'
import { useUploadFile } from '@/frontend/hooks/use-upload-file'

interface UseFileAttachmentsOptions {
  /** Pre-existing files from the server (edit mode). */
  existingFiles?: ExistingFile[]
}

interface UseFileAttachmentsReturn {
  /** All uploaded file keys (ready to include in the API payload). */
  fileKeys: FileKey[]
  /** Whether files are currently being uploaded. */
  uploading: boolean
  /**
   * Uploads any pending files and returns the complete list of file keys.
   * Call this in `beforeSubmit`.
   */
  uploadPending: () => Promise<FileKey[]>
  /** IDs of existing files marked for removal (edit mode). */
  removeFileIds: string[]
  /** Resets all state — call on form success. */
  reset: () => void
  /** Props to spread onto `<FileAttachments>`. */
  attachmentProps: FileAttachmentProps
  /** Error message when total file count exceeds the limit. Use to guard against submission. */
  fileCountError: string | undefined
}

/** Manages file attachment state and uploads for forms with file attachments. */
export function useFileAttachments(options?: UseFileAttachmentsOptions): UseFileAttachmentsReturn {
  const { mutateAsync: uploadFile } = useUploadFile()
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [uploadedKeys, setUploadedKeys] = useState<FileKey[]>([])
  const [uploading, setUploading] = useState(false)
  const [removedFileIds, setRemovedFileIds] = useState<Set<string>>(new Set())

  const { existingFiles } = options ?? {}

  /** Total number of files currently attached (existing + uploaded + pending). */
  const totalFileCount =
    (existingFiles?.length ?? 0) - removedFileIds.size + uploadedKeys.length + pendingFiles.length

  const uploadPending = async (): Promise<FileKey[]> => {
    let allFileKeys = [...uploadedKeys]
    if (pendingFiles.length > 0) {
      setUploading(true)
      try {
        const newKeys = await Promise.all(pendingFiles.map((f) => uploadFile(f)))
        allFileKeys = [...allFileKeys, ...newKeys]
        setUploadedKeys(allFileKeys)
        setPendingFiles([])
      } finally {
        setUploading(false)
      }
    }
    return allFileKeys
  }

  const reset = () => {
    setUploadedKeys([])
    setPendingFiles([])
    setRemovedFileIds(new Set())
  }

  const fileCountError =
    totalFileCount > MAX_FILE_ATTACHMENTS
      ? `Too many files attached — maximum ${MAX_FILE_ATTACHMENTS} allowed`
      : undefined

  return {
    fileKeys: uploadedKeys,
    uploading,
    uploadPending,
    removeFileIds: Array.from(removedFileIds),
    reset,
    fileCountError,
    attachmentProps: {
      pendingFiles,
      uploadedKeys,
      uploading,
      onAdd: (files) => setPendingFiles((prev) => [...prev, ...files]),
      onRemovePending: (file) => setPendingFiles((prev) => prev.filter((f) => f !== file)),
      existingFiles,
      removedFileIds,
      onRemoveExisting: (id) => setRemovedFileIds((prev) => new Set(prev).add(id)),
      onRestoreExisting: (id) =>
        setRemovedFileIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        }),
      fileCountError,
    },
  }
}
